// =============================================================================
// FILE: packages/renderer/src/config/demoMode.ts
// PURPOSE: "Demo Mode" — a founder-facing switch that injects a realistic
//   sample project ("Sierra Manor") into the web-preview mocks so the
//   dashboard, System Overview, and One-Click Submittal Set all show
//   believable numbers for launch videos and live demos.
//
//   Sierra Manor spec (as requested): 3 buildings · 10 racks · 100 cable IDs.
// =============================================================================

import type { DashboardStats } from '../hooks/useDashboard.js';

const DEMO_KEY = 'rcd2-demo-mode';           // 'on' | 'off'
export const DEMO_EVENT = 'r2:demo-mode';    // dispatched on toggle
export const SIERRA_MANOR_PROJECT_NAME = 'Sierra Manor';

export function isDemoMode(): boolean {
  try { return localStorage.getItem(DEMO_KEY) === 'on'; } catch { return false; }
}

export function setDemoMode(on: boolean): void {
  try { localStorage.setItem(DEMO_KEY, on ? 'on' : 'off'); } catch { /* noop */ }
  if (typeof window !== 'undefined') {
    window.dispatchEvent(new CustomEvent(DEMO_EVENT, { detail: { on } }));
  }
}

// ─── Sierra Manor sample dataset ─────────────────────────────────────────────
// 100 cable IDs  = 24 fiber + 76 copper = 100 connections
// 480 ports      = 20 patch panels × 24 ports

export const SIERRA_MANOR_STATS: DashboardStats = {
  buildings:     3,
  floors:        9,
  rooms:         28,
  telecomSpaces: 12,
  racks:         10,
  assets:        142,
  switches:      12,
  patchPanels:   20,
  ports:         480,
  outlets:       96,
  connections:   100,
  fiberCables:   24,
  copperCables:  76,
  projects:      1,
  documents:     18,
  openClashes:   2,
  generatedAt:   new Date().toISOString(),
};

export interface DemoActivityEntry {
  timestamp: string;
  tableName: string;
  operation: 'INSERT' | 'UPDATE' | 'DELETE';
  recordId:  string;
  userEmail: string | null;
}

/** A believable recent-activity trail for Sierra Manor. */
export function sierraManorActivity(): DemoActivityEntry[] {
  const now = Date.now();
  const min = 60_000;
  const rows: Array<[number, string, DemoActivityEntry['operation'], string]> = [
    [2,   'connections',       'INSERT', 'SM-CBL-100'],
    [6,   'connections',       'INSERT', 'SM-CBL-099'],
    [14,  'assets',            'INSERT', 'SM-AP-042'],
    [23,  'racks',             'INSERT', 'SM-IDF-B3-01'],
    [38,  'document_register', 'INSERT', 'SM-SUB-0018'],
    [55,  'clash_results',     'INSERT', 'SM-CLASH-002'],
    [72,  'assets',            'UPDATE', 'SM-SW-012'],
    [96,  'buildings',         'INSERT', 'SM-BLDG-C'],
    [130, 'telecom_spaces',    'INSERT', 'SM-TR-B2-04'],
    [165, 'projects',          'INSERT', 'SIERRA-MANOR'],
  ];
  return rows.map(([mins, table, op, id]) => ({
    timestamp: new Date(now - mins * min).toISOString(),
    tableName: table,
    operation: op,
    recordId:  id,
    userEmail: 'john.bismonte@gmail.com',
  }));
}
